'use client';

import React, { memo, useMemo } from 'react';
import { ExperienceType } from '../types';
import { typography } from '@/design-system/tokens/typography';
import Border from '../../common/border';

interface ExperienceInclusionsProps {
  experience: unknown;
  type: ExperienceType;
}

// Memoized checkmark icon component for better performance
const CheckIcon = memo(() => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 20 20'
    fill='none'
    className='text-green-600 mt-0.5 flex-shrink-0'
    aria-hidden='true'
  >
    <path
      d='M16.667 5L7.5 14.167 3.333 10'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
));

CheckIcon.displayName = 'CheckIcon';

export const ExperienceInclusions: React.FC<ExperienceInclusionsProps> = ({
  experience,
  type,
}) => {
  // Memoize inclusions extraction to prevent recalculation on every render
  // Must be called before any early returns to follow React hooks rules
  const inclusions = useMemo(() => {
    if (!experience) return [];
    const exp = experience as {
      inclusions?: Array<
        | string
        | {
            title?: string;
            name?: string;
            description?: string;
            [key: string]: unknown;
          }
      >;
      included?: Array<
        | string
        | {
            title?: string;
            name?: string;
            description?: string;
            [key: string]: unknown;
          }
      >;
      activityIncludes?: Array<
        | string
        | {
            title?: string;
            name?: string;
            description?: string;
            [key: string]: unknown;
          }
      >;
    };
    // For activities and stays, check activityIncludes first, then fall back to inclusions/included
    // For packages, check inclusions/included
    if (type === 'activity' || type === 'stay') {
      return exp?.activityIncludes || exp?.inclusions || exp?.included || [];
    }
    // For packages
    return exp?.inclusions || exp?.included || [];
  }, [experience, type]);

  if (!experience || !Array.isArray(inclusions) || inclusions.length === 0)
    return null;

  // Helper to extract string from inclusion (handles both string and object)
  const getInclusionText = (
    inclusion:
      | string
      | {
          title?: string;
          name?: string;
          description?: string;
          [key: string]: unknown;
        }
  ): string => {
    if (typeof inclusion === 'string') {
      return inclusion;
    }
    if (typeof inclusion === 'object' && inclusion !== null) {
      return (inclusion.title ||
        inclusion.name ||
        inclusion.description ||
        '') as string;
    }
    return '';
  };

  return (
    <div className='space-y-4'>
      <h2
        className='font-semibold text-gray-900'
        style={{
          fontSize: typography.size.lg, // 18px
          fontFamily: typography.fonts.primary,
          lineHeight: 'normal',
        }}
      >
        What All Is Included In The Package
      </h2>
      <div className='space-y-2'>
        {inclusions.map((inclusion, index) => {
          const inclusionText = getInclusionText(inclusion);
          if (!inclusionText) return null;
          return (
            <div key={index} className='flex items-start gap-3'>
              <CheckIcon />
              <p className='text-gray-700'>{inclusionText}</p>
            </div>
          );
        })}
      </div>
      <div className='pt-4'>
        <Border />
      </div>
    </div>
  );
};
